import { Controller } from "@hotwired/stimulus";
import { Turbo } from "@hotwired/turbo-rails";
import { keepFiles, filesFromDrop, visibleFiles, humanSize, interpolate, csrfToken } from "./bulk_file_store";

// The first page of a bulk upload (15/10/2026): files or whole folders are
// chosen, read here (count, size, too heavy, no digit for a matricule), and
// only their names and sizes are sent; the search page follows.
export default class extends Controller {
  static targets = ["zone", "summary", "count", "size", "heavy", "unnamed", "list", "error", "start", "startLabel"];
  static values = { createUrl: String, maxSize: Number, maxItems: Number, extensions: Array, messages: Object };

  static SHOWN = 60;

  connect() {
    this.files = [];
  }

  over(event) {
    event.preventDefault();
    this.zoneTarget.classList.add("is-over");
  }

  leave() {
    this.zoneTarget.classList.remove("is-over");
  }

  async drop(event) {
    event.preventDefault();
    this.leave();
    this.add(await filesFromDrop(event.dataTransfer));
  }

  picked(event) {
    this.add(Array.from(event.target.files || []));
    event.target.value = "";
  }

  clear() {
    this.files = [];
    this.render();
  }

  add(files) {
    const known = new Set(this.files.map((file) => this.keyOf(file)));
    visibleFiles(files).forEach((file) => {
      const key = this.keyOf(file);
      if (!known.has(key)) {
        known.add(key);
        this.files.push(file);
      }
    });
    this.render();
  }

  keyOf(file) {
    return `${file.name}|${file.size}`;
  }

  isHeavy(file) {
    return file.size > this.maxSizeValue;
  }

  isUnnamed(file) {
    return !/\d/.test(file.name.replace(/\.[^.]+$/, ""));
  }

  // Another format, or an empty file (a scan that failed).
  isRefused(file) {
    const extension = (file.name.split(".").pop() || "").toLowerCase();
    return file.size === 0 || !this.extensionsValue.includes(extension);
  }

  render() {
    const m = this.messagesValue;
    const count = this.files.length;
    this.summaryTarget.hidden = count === 0;
    if (count === 0) return;

    this.countTarget.textContent = count.toLocaleString();
    this.sizeTarget.textContent = humanSize(this.files.reduce((sum, file) => sum + file.size, 0));
    this.heavyTarget.textContent = this.files.filter((file) => this.isHeavy(file) || this.isRefused(file)).length.toLocaleString();
    this.unnamedTarget.textContent = this.files.filter((file) => this.isUnnamed(file)).length.toLocaleString();

    this.listTarget.replaceChildren(...this.files.slice(0, this.constructor.SHOWN).map((file) => this.row(file)));
    if (count > this.constructor.SHOWN) {
      const more = document.createElement("li");
      more.className = "ds-bulk-list__more";
      more.textContent = interpolate(m.more, { count: (count - this.constructor.SHOWN).toLocaleString() });
      this.listTarget.append(more);
    }

    const tooMany = count > this.maxItemsValue;
    this.errorTarget.hidden = !tooMany;
    this.errorTarget.textContent = tooMany ? interpolate(m.too_many, { count: this.maxItemsValue.toLocaleString(), chosen: count.toLocaleString() }) : "";
    this.startTarget.disabled = tooMany;
    this.startLabelTarget.textContent = interpolate(m.start_search, { count: count.toLocaleString() });
  }

  row(file) {
    const li = document.createElement("li");
    const name = document.createElement("span");
    name.className = "ds-bulk-list__name";
    name.textContent = file.webkitRelativePath || file.name;
    const size = document.createElement("span");
    size.className = "ds-bulk-list__size";
    size.textContent = humanSize(file.size);
    li.append(name, size);

    const badge = this.isRefused(file) ? "refused" : this.isHeavy(file) ? "heavy" : this.isUnnamed(file) ? "unnamed" : null;
    if (badge) {
      const chip = document.createElement("span");
      chip.className = `ds-chip ds-chip--sm ${badge === "unnamed" ? "ds-chip--info" : "ds-chip--warning"}`;
      chip.textContent = this.messagesValue[`badge_${badge}`];
      li.append(chip);
    }
    return li;
  }

  async start() {
    const m = this.messagesValue;
    this.startTarget.disabled = true;
    this.startTarget.classList.add("is-loading");
    this.startLabelTarget.textContent = m.sending_list;
    this.errorTarget.hidden = true;

    try {
      const response = await fetch(this.createUrlValue, {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrfToken() },
        body: JSON.stringify({ files: this.files.map((file) => ({ name: file.name, size: file.size })) }),
      });
      const json = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(json.error || m.network_error);

      keepFiles(json.id, this.files);
      Turbo.visit(json.url);
    } catch (error) {
      this.errorTarget.hidden = false;
      this.errorTarget.textContent = error.message || m.network_error;
      this.startTarget.disabled = false;
      this.startTarget.classList.remove("is-loading");
      this.startLabelTarget.textContent = interpolate(m.start_search, { count: this.files.length.toLocaleString() });
    }
  }
}
